import { ReactNode, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Layer, Map, MapRef, Popup, Source } from 'react-map-gl/mapbox';
import { LngLatBounds, MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FeatureCollection } from 'geojson';
import { Box, useTheme } from '@mui/material';
import {
  filteredDeviceListSelector,
  selectedDeviceStateAtom,
} from '../../../models/atoms/device_atoms';
import { device_controller } from '../../../controllers/device_controller';
import {
  defaultLocationAtom,
  mapStyleAtom,
} from '../../../models/atoms/ui_atoms';
import '../../styles/map.css';
import { deviceListEntryInterface } from '../../../models/interfaces/device_interfaces';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { NAVBAR_HEIGHT } from '../navbar/Navbar';
import { DeviceInfoCard } from './DeviceInfoCard';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoibWF4LXdpY2toYW0iLCJhIjoiY2w4bjlnNGIwMGY0NTN1b2FtMDZ4dWRqMSJ9.qbF5dOznUZ0eWlOay_3V4Q';

export function DevicesMap() {
  const filteredDevices = useRecoilValue(filteredDeviceListSelector);
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const mapRef = useRef<MapRef>(null);
  const defaultLocation = useRecoilValue(defaultLocationAtom);
  const style = useRecoilValue(mapStyleAtom);
  const { palette } = useTheme();
  const isMobile = useIsMobile();
  const [popupCoords, setPopupCoords] = useState<[number, number] | null>(null);
  const [popupContent, setPopupContent] = useState<ReactNode | null>(null);

  const initialViewState = selectedDevice
    ? {
        longitude: selectedDevice.longitude,
        latitude: selectedDevice.latitude,
        zoom: 12,
      }
    : {
        longitude: defaultLocation.coordinates[0],
        latitude: defaultLocation.coordinates[1],
        zoom: 5,
      };

  const devices = filteredDevices.devices.filter(
    device => device.device_id !== selectedDevice?.device_id,
  );
  if (selectedDevice) {
    // deviceListEntryInterface is a subset of deviceInterface, so this can work
    // however, should consider transforming it into a deviceListEntryInterface
    devices.push(selectedDevice as deviceListEntryInterface);
  }

  const devicesDots: FeatureCollection = {
    type: 'FeatureCollection',
    features: devices.map(device => ({
      type: 'Feature',
      id: device.device_id,
      geometry: {
        type: 'Point',
        coordinates: [device.longitude, device.latitude],
      },
      properties: {
        colour:
          selectedDevice?.device_id === device.device_id
            ? palette.selectedDevice.main
            : palette.primary.main,
        // selected: selectedDevice?.device_id === device.device_id,
      },
    })),
  };

  // function loadImage(e: MapEvent) {
  //     const logoImg = new Image();
  //     logoImg.onload = () => {
  //         e.target.addImage('mhm-logo', logoImg, { sdf: true });
  //     }
  //     // use navbarLogoURL from theme.assets
  //     logoImg.src = navbarLogoURL;
  // }

  function handleClick(e: MapMouseEvent) {
    // all clicks on map will fire this callback
    // also, console.log(e) doesn's show `features` in it, but console.log(e.features) does...
    if (!e.features?.length) {
      return;
    }

    const clickedDeviceID = e.features[0].id;
    if (!clickedDeviceID) {
      return;
    }
    device_controller.select_device(String(clickedDeviceID));
    closePopup();
  }
  function handleHover(e: MapTouchEvent | MapMouseEvent) {
    if (!e.features || !e.features?.length) {
      return;
    }
    const device_id = e.features[0].id;
    if (!device_id) {
      return;
    }
    const device = filteredDevices.devices.find(
      d => d.device_id === String(device_id),
    );
    if (!device) {
      return;
    }
    setPopupCoords(e.lngLat.toArray() as [number, number]);
    setPopupContent(
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: 0.8 * (mapRef.current?.getContainer().clientHeight || 0),
        }}
      >
        <DeviceInfoCard device={device} onClose={closePopup} />
      </Box>,
    );
  }
  function closePopup() {
    setPopupCoords(null);
    setPopupContent(null);
  }

  useEffect(() => {
    if (!selectedDevice) {
      mapRef.current?.jumpTo({
        center:
          defaultLocation.coordinates.length >= 2
            ? [defaultLocation.coordinates[0], defaultLocation.coordinates[1]]
            : [0.0, 0.0],
        zoom: 5,
      });
      return;
    }

    mapRef.current?.flyTo({
      center: [selectedDevice.longitude, selectedDevice.latitude],
      zoom: 12,
    });
  }, [selectedDevice, defaultLocation]);

  useEffect(() => {
    if (!filteredDevices.devices.length || selectedDevice) {
      return;
    }

    const bounds = filteredDevices.devices.reduce(
      (acc, device) => acc.extend([device.longitude, device.latitude]),
      new LngLatBounds(),
    );
    mapRef.current?.fitBounds(bounds, {
      maxZoom: 12,
      duration: 0,
      padding: { top: 20, right: 50, bottom: 20, left: 50 },
    });
  }, [filteredDevices, selectedDevice]);

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={MAPBOX_TOKEN}
      initialViewState={initialViewState}
      mapStyle={
        style === 'satellite'
          ? 'mapbox://styles/mapbox/satellite-streets-v11'
          : 'mapbox://styles/mapbox/streets-v11'
      }
      interactiveLayerIds={['devices']}
      cursor="pointer"
      reuseMaps
      // onLoad={loadImage}
      onClick={handleClick}
      onTouchStart={handleHover}
      onMouseEnter={handleHover}
      onMouseLeave={closePopup}
      minPitch={0}
      maxPitch={0}
      touchPitch={false}
      style={{
        borderRadius: isMobile ? 0 : 5,
        width: '100%',
        height: isMobile ? `calc(100vh - ${NAVBAR_HEIGHT}px)` : '100%',
      }}
      onIdle={() => {
        mapRef.current?.resize();
      }}
    >
      <Source type="geojson" data={devicesDots}>
        <Layer
          id="devices"
          type="circle"
          // filter={['==', 'selected', false]}
          paint={{
            'circle-radius': 6,
            'circle-color': ['get', 'colour'],
          }}
        />
        {/* <Layer
                    id="selected-device"
                    type="symbol"
                    filter={['==', 'selected', true]}
                    layout={{
                        'icon-image': 'mhm-logo',
                        // icon size & offset set to make centre circle of logo match the position of the dot
                        'icon-size': 0.08,
                        'icon-offset': [0, -27],
                    }}
                    paint={{ 'icon-color': '#000099' }}
                /> */}
      </Source>
      {popupCoords && (
        <Popup
          longitude={popupCoords[0]}
          latitude={popupCoords[1]}
          closeButton={false}
          onClose={closePopup}
        >
          {popupContent}
        </Popup>
      )}
    </Map>
  );
}
